import React, { FC, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Container } from "@montana/shared/container";
import { Transition } from "framer-motion";
import { RichText } from "@montana/shared/rich-text";
import { SvgIcon } from "@montana/shared/svg-icon";
import { getTextColorFor } from "@montana/helpers/theme-color.helper";
import { INoticeBar } from "@montana/models/generated/cms.interface";
import {
    getLocalStorageItem,
    setLocalStorageItem
} from "@montana/helpers/localstorage.helper";
import { usePage } from "@montana/containers/page";
import {
    CartPageKind,
    CheckoutB2CPageKind,
    CheckoutPageKind,
    ConfirmationPageKind,
    ReceiptPageKind,
    ShippingPageKind
} from "@montana/pages/dynamic-page.component";
import { useDynamicSettings } from "@montana/containers/dynamic-settings";
import { interceptChildLinkClick } from "@montana/helpers/link-hijack.helper";

export interface INoticeBarProps {
    hide?: boolean;
    onClose?: () => void;
}

const transition: Transition = {
    type: "spring",
    stiffness: 500,
    damping: 50
};

const MON_NOTICEBAR_SEEN = "MON_NOTICEBAR_SEEN";

const hasNoNoticePageKind = (kind: string): boolean => {
    switch (kind) {
        case CheckoutPageKind:
        case CheckoutB2CPageKind:
        case ReceiptPageKind:
        case CartPageKind:
        case ShippingPageKind:
        case ConfirmationPageKind:
            return true;
        default:
            return false;
    }
};

export const NoticeBar: FC<INoticeBarProps> = ({
    hide,
    onClose
}: INoticeBarProps) => {
    const { kind, epiId } = usePage();
    const { noticeBarViewModel: noticeBar } = useDynamicSettings();
    const { excludeOn, onlyIncludeOn, backgroundColor, text } = noticeBar ?? {} as INoticeBar;

    const [showNoticeBar, setShowNoticeBar] = useState(false);

    useEffect(() => {
        const isDisallowed = hasNoNoticePageKind(kind) ||
            ((onlyIncludeOn?.length ?? 0) > 0 && !onlyIncludeOn?.find(id => epiId === id)) ||
            excludeOn?.some(id => id === epiId);

        if (
            !!noticeBar &&
            !isDisallowed &&
            (!noticeBar.refererDependency ||
                !!document.referrer?.includes(noticeBar.refererDependency))
        ) {
            const seenNoticeBar = getLocalStorageItem(MON_NOTICEBAR_SEEN);

            if (!seenNoticeBar || seenNoticeBar !== JSON.stringify(noticeBar)) {
                setShowNoticeBar(true);
            } else {
                if (!isDisallowed) {
                    onClose && onClose();
                } else {
                    setShowNoticeBar(true);
                }
            }
        } else {
            if (!isDisallowed) {
                onClose && onClose();
            } else {
                setShowNoticeBar(false);
            }
        }
    }, [noticeBar, kind, epiId]);

    useEffect(() => {
        if (showNoticeBar && hide) {
            setShowNoticeBar(false);
        }
    }, [hide]);

    const close = () => {
        setLocalStorageItem(MON_NOTICEBAR_SEEN, JSON.stringify(noticeBar));
        onClose && onClose();
    };

    const onChildLinkClick = interceptChildLinkClick(close);

    return (
        <AnimatePresence>
            {!!text && showNoticeBar && (
                <motion.aside
                    initial={{ height: "auto" }}
                    animate={{ height: "auto" }}
                    exit={{ height: "0px" }}
                    transition={transition}
                    className="notice-bar"
                    style={
                        backgroundColor
                            ? {
                                backgroundColor: backgroundColor,
                                color: getTextColorFor(backgroundColor)
                            }
                            : {}
                    }
                >
                    <Container className="notice-bar__content" onClick={onChildLinkClick}>
                        <RichText
                            disableAppear={true}
                            text={text}
                            translation={true}
                            className="notice-bar__text"
                        />
                        <button
                            className="notice-bar__close"
                            onClick={() => close()}
                            style={{
                                backgroundColor
                            }}
                        >
                            <SvgIcon
                                iconName="close"
                                className="notice-bar__close-svg"
                            />
                        </button>
                    </Container>
                </motion.aside>
            )}
        </AnimatePresence>
    );
};
